/** FIX-286: completing the donor appliance from the harvest flow. */
import { donorFromAppliance, type DonorAppliance } from "@/lib/sparePartDonor";

export const HARVEST_APPLIANCE_CATEGORIES = [
  "Dishwasher",
  "Washing Machine",
  "Dryer",
  "Washer Dryer",
  "Household Refrigerator",
] as const;

export type CompletableField = "category" | "brand" | "model" | "serial";

export const FIELD_COLUMN: Record<CompletableField, keyof Omit<DonorAppliance, "id">> = {
  category: "category",
  brand: "brand",
  model: "model_number",
  serial: "serial_number",
};

/** Missing donor fields on the appliance row (whitespace counts as missing). */
export function missingApplianceFields(appliance: DonorAppliance | null | undefined): CompletableField[] {
  if (!appliance?.id) return [];
  const r = donorFromAppliance(appliance, "work-order");
  if (!("missing" in r)) return [];
  return r.missing.filter((f): f is CompletableField => f in FIELD_COLUMN);
}

export const hasCategory = (a: DonorAppliance | null | undefined) => !!(a?.category ?? "").trim();

/**
 * Recommendation request for a DB-backed appliance: null (no call) until the
 * appliance row is loaded and has a category — without a category the engine
 * would return parts from every category.
 */
export function recommendationRequestFor<T>(
  isDbBacked: boolean,
  donor: DonorAppliance | null | undefined,
  request: T,
): T | null {
  if (!isDbBacked) return request;
  if (!donor || !hasCategory(donor)) return null;
  return request;
}

/** Fields the dialog shows: missing ones, plus the brand for review when the sheet doesn't know it. */
export function fieldsToShow(
  appliance: DonorAppliance | null | undefined,
  opts: { reviewBrand?: boolean } = {},
): { fields: CompletableField[]; review: CompletableField[] } {
  const fields = missingApplianceFields(appliance);
  const review: CompletableField[] = [];
  if (opts.reviewBrand && appliance?.id && !fields.includes("brand")) {
    review.push("brand");
    const order: CompletableField[] = ["category", "brand", "model", "serial"];
    return { fields: order.filter((f) => f === "brand" || fields.includes(f)), review };
  }
  return { fields, review };
}

/** Ask to review the brand only when the sheet says it's unknown and this brand isn't confirmed yet. */
export function needsBrandReview(
  brandRecognised: boolean | null | undefined,
  brand: string | null | undefined,
  confirmedFor: string | null | undefined,
): boolean {
  if (brandRecognised !== false) return false;
  const b = (brand ?? "").trim();
  if (!b) return false;
  return (confirmedFor ?? "").trim().toLowerCase() !== b.toLowerCase();
}

/** Only fields whose trimmed value changed are written (and logged). */
export function changedPatch(
  appliance: DonorAppliance,
  fields: CompletableField[],
  values: Record<CompletableField, string>,
): Partial<Record<keyof Omit<DonorAppliance, "id">, string>> {
  const patch: Partial<Record<keyof Omit<DonorAppliance, "id">, string>> = {};
  for (const f of fields) {
    const col = FIELD_COLUMN[f];
    const next = values[f].trim().slice(0, 100);
    if (next && next !== (appliance[col] ?? "").trim()) patch[col] = next;
  }
  return patch;
}

/** FIX-286: derived appliances.name ("brand model", empty parts skipped) when brand or model changed; else null. */
export function derivedNameFor(
  appliance: Pick<DonorAppliance, "brand" | "model_number">,
  patch: Partial<Record<"brand" | "model_number", string>>,
): string | null {
  if (patch.brand === undefined && patch.model_number === undefined) return null;
  const brand = (patch.brand ?? appliance.brand ?? "").trim();
  const model = (patch.model_number ?? appliance.model_number ?? "").trim();
  return [brand, model].filter(Boolean).join(" ") || null;
}
